import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import * as xlsx from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';

import * as billCollectApi from '../../../lib/api/billCollect';

// 수금수단. PMS_BILL_INV.PROC_TYP 가 이미 있지만 실제로는 아무도 쓰지 않아 선택 입력으로 둔다.
const COLLECT_TYPES = [
  { value: '', label: '선택 안 함' },
  { value: 'CASH', label: '현금' },
  { value: 'NOTE', label: '어음' },
  { value: 'OFFSET', label: '상계' },
  { value: 'CARD', label: '카드' },
  { value: 'ETC', label: '기타' },
];

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};

const toNumber = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const typeLabel = (value) =>
  COLLECT_TYPES.find((t) => t.value === value)?.label || '-';

/**
 * 세금계산서 부분 수금.
 *
 * 대금이 한 번에 전액 들어온다는 전제로 되어 있던 것을, 나눠 들어오는 실제에 맞춘다.
 * 발행액·수금액·잔액은 서버가 계산해서 내려준다 — 화면에서 따로 더하면 숫자가 서로 달라진다.
 */
const BillCollectDialog = ({ open, onClose, invUuid, invTyp, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();

  // 매입은 우리가 주는 돈이다. 화면에 "수금" 이라고 뜨면 반대로 읽힌다.
  const isPurchase = invTyp === '매입';
  const term = isPurchase
    ? { title: '지급 내역', amount: '지급액', date: '지급일자', done: '지급 완료',
        pending: '미지급', account: '출금 계좌', handler: '지급 처리자', action: '지급' }
    : { title: '수금 내역', amount: '수금액', date: '수금일자', done: '수금 완료',
        pending: '미수금', account: '입금 계좌', handler: '입금 처리자', action: '수금' };

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [collectDt, setCollectDt] = useState(dayjs());
  const [collectAmt, setCollectAmt] = useState('');
  const [collectTyp, setCollectTyp] = useState('');
  const [remark, setRemark] = useState('');
  const [accountInfo, setAccountInfo] = useState('');
  const [handlerNm, setHandlerNm] = useState('');

  const showError = useCallback((err, fallback) => {
    const message = err?.response?.data?.message || err?.message || fallback;
    enqueueSnackbar(message, { variant: 'error' });
  }, [enqueueSnackbar]);

  const load = useCallback(async () => {
    if (!invUuid) return;
    setLoading(true);
    try {
      const res = await billCollectApi.getCollectSummary(invUuid);
      setSummary(res?.data?.data || null);
    } catch (err) {
      showError(err, '수금 내역을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [invUuid, showError]);

  useEffect(() => {
    if (open) {
      setCollectDt(dayjs());
      setCollectAmt('');
      setCollectTyp('');
      setRemark('');
      setAccountInfo('');
      setHandlerNm('');
      load();
    }
  }, [open, load]);

  const remain = toNumber(summary?.remainAmt);
  const inputAmt = toNumber(collectAmt);
  // 잔액을 넘는 입력은 서버가 막지만, 누르기 전에 보이는 편이 낫다.
  const overRemain = inputAmt > remain;

  const activeCollects = useMemo(
    () => (summary?.collects || []).filter((c) => c.useYn === 'Y'),
    [summary],
  );

  const handleAdd = async () => {
    if (!collectDt || !collectDt.isValid()) {
      enqueueSnackbar('수금일자를 입력해 주세요.', { variant: 'warning' });
      return;
    }
    if (inputAmt <= 0) {
      enqueueSnackbar('수금액을 입력해 주세요.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await billCollectApi.addCollect(invUuid, {
        collectDt: collectDt.format('YYYY-MM-DD'),
        collectAmt: String(inputAmt),
        collectTyp: collectTyp || null,
        remark: remark || null,
        accountInfo: accountInfo || null,
        // 비워두면 서버가 등록한 사람 이름으로 채운다.
        handlerNm: handlerNm || null,
      });
      setSummary(res?.data?.data || null);
      setCollectAmt('');
      setRemark('');
      enqueueSnackbar(`${term.action}을 등록했습니다.`, { variant: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      showError(err, '수금 등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * 이 계산서의 수금 내역만 엑셀로 내려받는다.
   * 화면에 보이는 표 그대로 나가고, 대사에 필요한 취소분·사유까지 포함한다.
   * (전체 기간 조회는 /billcollect 화면에서 따로 받는다)
   */
  const handleExcel = () => {
    const rows = summary?.collects || [];
    if (rows.length === 0) {
      enqueueSnackbar('내려받을 내역이 없습니다.', { variant: 'warning' });
      return;
    }
    const data = rows.map((c) => ({
      [term.date]: c.collectDt || '',
      [term.amount]: toNumber(c.collectAmt),
      수단: typeLabel(c.collectTyp),
      [term.account]: c.accountInfo || '',
      처리자: c.handlerNm || '',
      비고: c.remark || '',
      등록자: c.crtNm || '',
      등록일시: c.crtDt || '',
      상태: c.useYn === 'Y' ? '유효' : '취소',
      취소사유: c.useYn === 'Y' ? '' : (c.cancelReason || ''),
    }));

    const sheet = xlsx.utils.json_to_sheet(data);
    const book = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(book, sheet, term.title);
    xlsx.writeFile(book, `${term.title}_${summary?.invNo || ''}.xlsx`);
    enqueueSnackbar('엑셀을 내려받았습니다.', { variant: 'success' });
  };

  const handleCancel = async (collect) => {
    // 삭제가 아니라 취소다. 사유를 남겨야 나중에 금액 흐름을 설명할 수 있다.
    const reason = window.prompt('취소 사유를 입력해 주세요.');
    if (reason === null) return;
    if (!reason.trim()) {
      enqueueSnackbar('취소 사유를 입력해 주세요.', { variant: 'warning' });
      return;
    }
    try {
      const res = await billCollectApi.cancelCollect(collect.collectUuid, reason.trim());
      setSummary(res?.data?.data || null);
      enqueueSnackbar(`${term.action}을 취소했습니다.`, { variant: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      showError(err, '수금 취소에 실패했습니다.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {term.title}
        {summary?.invNo && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
            {summary.invNo}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 3,
                px: 1.5,
                py: 1.5,
                mb: 2,
                borderRadius: 1,
                backgroundColor: 'action.hover',
              }}
            >
              <Summary label="발행액" value={summary?.invTot} />
              <Summary label={term.amount} value={summary?.collectedAmt} />
              <Summary label="잔액" value={summary?.remainAmt} strong />
              <Box sx={{ flexGrow: 1 }} />
              <Chip
                label={summary?.completed ? term.done : term.pending}
                color={summary?.completed ? 'success' : 'warning'}
                variant={summary?.completed ? 'outlined' : 'filled'}
                sx={{ fontWeight: 700 }}
              />
            </Box>

            {/* 과거 건은 내역을 옮기지 않아서 금액만 있고 줄이 없다. 그대로 두면 "왜 내역이 없지" 가 된다. */}
            {summary?.legacyCompleted && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, px: 1.5 }}>
                이 건은 이전 방식(전액 수금 완료)으로 처리되어 세부 내역이 없습니다.
              </Typography>
            )}

            {!summary?.completed && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2, px: 0.5 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={term.date}
                    value={collectDt}
                    onChange={setCollectDt}
                    format="YYYY-MM-DD"
                    slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                  />
                </LocalizationProvider>
                <TextField
                  label={term.amount}
                  size="small"
                  value={fmt(collectAmt)}
                  onChange={(e) => setCollectAmt(String(e.target.value).replace(/[^0-9]/g, ''))}
                  error={overRemain}
                  helperText={overRemain ? `잔액 ${fmt(remain)} 초과` : ' '}
                  inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                  sx={{ width: 150 }}
                />
                <TextField
                  select
                  label={`${term.action}수단`}
                  size="small"
                  value={collectTyp}
                  onChange={(e) => setCollectTyp(e.target.value)}
                  sx={{ width: 130 }}
                >
                  {COLLECT_TYPES.map((t) => (
                    <MenuItem key={t.value || 'none'} value={t.value}>{t.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={term.account}
                  size="small"
                  value={accountInfo}
                  onChange={(e) => setAccountInfo(e.target.value)}
                  sx={{ width: 180 }}
                />
                <TextField
                  label={term.handler}
                  size="small"
                  placeholder="비우면 본인"
                  value={handlerNm}
                  onChange={(e) => setHandlerNm(e.target.value)}
                  sx={{ width: 130 }}
                />
                <TextField
                  label="비고"
                  size="small"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  sx={{ flex: '1 1 180px', minWidth: 160 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAdd}
                  disabled={saving || inputAmt <= 0 || overRemain}
                  sx={{ height: 40 }}
                >
                  등록
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 1.5 }} />

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{term.date}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{term.amount}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>수단</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{term.account}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>처리자</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>비고</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>등록자</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summary?.collects || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                        {term.title}이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.collects.map((c) => {
                      const cancelled = c.useYn !== 'Y';
                      return (
                        <TableRow
                          key={c.collectUuid}
                          sx={cancelled ? { backgroundColor: 'action.hover', color: 'text.disabled' } : undefined}
                        >
                          <TableCell sx={cancelled ? { textDecoration: 'line-through' } : undefined}>
                            {c.collectDt}
                          </TableCell>
                          <TableCell align="right" sx={cancelled ? { textDecoration: 'line-through' } : { fontWeight: 600 }}>
                            {fmt(c.collectAmt)}
                          </TableCell>
                          <TableCell>{typeLabel(c.collectTyp)}</TableCell>
                          <TableCell>{c.accountInfo || '-'}</TableCell>
                          <TableCell>{c.handlerNm || '-'}</TableCell>
                          <TableCell>
                            {cancelled ? (
                              <Typography variant="caption" color="text.secondary">
                                취소 · {c.cancelReason}
                              </Typography>
                            ) : (c.remark || '-')}
                          </TableCell>
                          <TableCell>{c.crtNm || '-'}</TableCell>
                          <TableCell align="center">
                            {!cancelled && (
                              <Button size="small" color="inherit" onClick={() => handleCancel(c)}>
                                취소
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {activeCollects.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 0.5 }}>
                유효 {activeCollects.length}건 · 취소분은 취소선으로 표시됩니다(기록은 남습니다).
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleExcel}
          disabled={loading || (summary?.collects || []).length === 0}
        >
          엑셀 다운로드
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" onClick={onClose}>닫기</Button>
      </DialogActions>
    </Dialog>
  );
};

function Summary({ label, value, strong }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography
        variant={strong ? 'subtitle1' : 'body2'}
        sx={{ fontWeight: strong ? 700 : 500, color: strong ? 'primary.main' : 'text.primary' }}
      >
        {fmt(value)}
      </Typography>
    </Box>
  );
}

export default BillCollectDialog;
